import type { ReactNode } from 'react';

/** Small presentational primitives shared across pages. */

export function Card({
  title,
  subtitle,
  actions,
  children,
  className = '',
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={`rounded-xl border border-border-subtle bg-surface-1 ${className}`}
    >
      {(title || actions) && (
        <header className="flex items-start justify-between gap-4 border-b border-border-subtle px-5 py-3.5">
          <div>
            {title && <h2 className="text-sm font-semibold text-ink-0">{title}</h2>}
            {subtitle && <p className="mt-0.5 text-xs text-ink-2">{subtitle}</p>}
          </div>
          {actions}
        </header>
      )}
      <div className="min-w-0 p-5">{children}</div>
    </section>
  );
}

export function StatTile({
  label,
  value,
  hint,
  tone = 'neutral',
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: 'neutral' | 'good' | 'warn' | 'bad';
}) {
  const toneClass =
    tone === 'good'
      ? 'text-good'
      : tone === 'warn'
        ? 'text-warn'
        : tone === 'bad'
          ? 'text-bad'
          : 'text-ink-0';

  return (
    <div className="rounded-lg border border-border-subtle bg-surface-2 px-4 py-3">
      <div className="text-[11px] font-medium uppercase tracking-wide text-ink-2">{label}</div>
      <div className={`tnum mt-1 text-xl font-semibold ${toneClass}`}>{value}</div>
      {hint && <div className="mt-0.5 text-xs text-ink-2">{hint}</div>}
    </div>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="flex items-center gap-3 text-sm text-ink-1" role="status" aria-live="polite">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-ink-2 border-t-accent" />
      {label && <span>{label}</span>}
    </div>
  );
}

export function ErrorState({
  title = 'Something went wrong',
  message,
  action,
}: {
  title?: string;
  message: string;
  action?: ReactNode;
}) {
  return (
    <div className="rounded-xl border border-border-subtle bg-surface-1 p-6 text-center">
      <h3 className="text-sm font-semibold text-ink-0">{title}</h3>
      <p className="mx-auto mt-2 max-w-md text-sm text-ink-1">{message}</p>
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
}

export function EmptyState({ message, action }: { message: string; action?: ReactNode }) {
  return (
    <div className="py-10 text-center">
      <p className="text-sm text-ink-2">{message}</p>
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
}

export function Button({
  children,
  onClick,
  disabled,
  variant = 'primary',
  type = 'button',
  title,
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  variant?: 'primary' | 'ghost' | 'danger';
  type?: 'button' | 'submit';
  title?: string;
}) {
  const base =
    'inline-flex items-center gap-2 rounded-lg px-3.5 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50';
  const variants = {
    primary: 'bg-accent text-surface-0 hover:bg-accent/85',
    ghost: 'border border-border-subtle bg-surface-2 text-ink-0 hover:bg-surface-3',
    danger: 'border border-bad/40 bg-transparent text-bad hover:bg-bad/10',
  } as const;

  return (
    <button type={type} onClick={onClick} disabled={disabled} title={title} className={`${base} ${variants[variant]}`}>
      {children}
    </button>
  );
}

/**
 * Full role names. The badge itself shows "Pos 4" for width, so the tooltip is
 * the only place the role is spelled out — abbreviating it there helped nobody.
 */
const POSITION_LABEL: Record<number, string> = {
  1: 'Carry',
  2: 'Mid',
  3: 'Offlane',
  4: 'Soft Support',
  5: 'Hard Support',
};

/**
 * Queued-together size. Hidden for solos — a column of "party of 1" is noise.
 *
 * A full 10-stack on a league match is the lobby, not a pub stack, so that
 * badge stays off league pages. Smaller parties on a league parse still show.
 */
export function PartyBadge({
  size,
  leagueId,
}: {
  size: number | null | undefined;
  leagueId?: number | null;
}) {
  if (size == null || size <= 1) return null;
  if (leagueId != null && size === 10) return null;
  return (
    <span
      className="rounded bg-surface-3 px-1.5 py-0.5 text-[11px] font-medium text-ink-1"
      title={`Queued in a party of ${size}`}
    >
      party of {size}
    </span>
  );
}

export function PositionBadge({ position }: { position: number | null | undefined }) {
  if (position == null) {
    return <span className="rounded bg-surface-3 px-1.5 py-0.5 text-[11px] text-ink-2">—</span>;
  }
  return (
    <span
      className="rounded bg-surface-3 px-1.5 py-0.5 text-[11px] font-medium text-ink-1"
      title={POSITION_LABEL[position]}
    >
      Pos {position}
    </span>
  );
}

/**
 * Confidence indicator.
 *
 * Shown wherever a derived number could be over-trusted — small samples and
 * inferred positions are unavoidable in this domain, and hiding that would be
 * misleading.
 */
export function ConfidenceDot({ value }: { value: number | null | undefined }) {
  if (value == null) return null;
  const tone = value >= 0.66 ? 'bg-good' : value >= 0.35 ? 'bg-warn' : 'bg-bad';
  const label = value >= 0.66 ? 'High' : value >= 0.35 ? 'Moderate' : 'Low';
  return (
    <span
      className="inline-flex items-center gap-1 text-[11px] text-ink-2"
      title={`${label} confidence (${(value * 100).toFixed(0)}%)`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${tone}`} />
      {label}
    </span>
  );
}

export function Bar({ value, max = 1 }: { value: number; max?: number }) {
  const width = Math.max(0, Math.min(100, (value / max) * 100));
  const tone = width >= 66 ? 'bg-good' : width >= 40 ? 'bg-warn' : 'bg-bad';
  return (
    <div
      className="h-1.5 w-full overflow-hidden rounded-full bg-surface-3"
      role="progressbar"
      aria-valuenow={Math.round(width)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        data-testid="bar-fill"
        className={`h-full rounded-full ${tone}`}
        style={{ width: `${width}%` }}
      />
    </div>
  );
}
